// Libraries & Node Modules
import algoliasearch from "algoliasearch/lite";
import { useState, useContext, useEffect } from "react";
import { renderToString } from "react-dom/server";
import Head from "next/head";
import { useSpecials } from "../../context/specials";

// Algolia
import {
  InstantSearch,
  Hits,
  Configure,
  InstantSearchSSRProvider,
  useInstantSearch,
  useClearRefinements,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import { getServerState } from "react-instantsearch-hooks-server";

// Icons
import MessageXmark from "../../components/icons/message-xmark.svg";
import ArrowRotateLeft from "../../components/icons/arrow-rotate-left.svg";

// Hooks & Context
import useMediaQuery from "../../hooks/useMediaQuery";
import { useNextRouterHandler, decodeParam } from "../../hooks/useNextRouterHandler";
import StoreContext from "../../context/store";

// Components
import ProductCard from "../../components/product/ProductCard";
// import ShopNav from "../../components/shop/ShopNav";
import Header from "../../components/shop/Header";
import Pagination from "../../components/shop/Pagination";
import CurrentRefinements from "../../components/shop/CurrentRefinements";

// Styles
import styles from "./shop.module.scss";
import productCardStyles from "../../components/product/ProductCard.module.scss";


import ShopNav from "../../components/shop/ShopNav";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);
const indexName = "menu-products-by-rating-production";

export async function getServerSideProps({ req, params }) {
  const protocol = req.headers.referer?.split("://")[0] || "https";
  const url = `${protocol}://${req.headers.host}${req.url}`;
  const initialStore = req.cookies.JANE_STORE || 4398;
  // Decode the category to handle URL encoding and normalize it to lowercase
  const category = decodeURIComponent(params.category).toLowerCase();

  // Query Algolia to get valid root_types facets (including subcategories)
  const index = searchClient.initIndex(indexName);

  const searchResult = await index.search("", {
    facets: ["root_types"],
    facetFilters: [`store_id:${initialStore}`],
  });

  // Normalize the facet keys to lowercase for comparison
  const validCategories = Object.keys(searchResult.facets.root_types || {}).map((key) =>
    key.toLowerCase()
  );

  // Check if the normalized category exists
  if (!validCategories.includes(category)) {
    return {
      notFound: true,  // Return 404 if the category is not valid
    };
  }

  // Find the correctly capitalized category to use on the page
  const correctCategory = Object.keys(searchResult.facets.root_types).find(
    (key) => key.toLowerCase() === category
  );

  const serverState = await getServerState(
    <CategoryShopPage
      url={url}
      initialStore={initialStore}
      category={category}
    />,
    { renderToString }
  );

  return {
    props: {
      serverState,
      url,
      initialStore,
      category,
    },
  };
}

export default function CategoryShopPage({ serverState, url, initialStore, category }) {
  const { initialUiState, NextRouterHandler } = useNextRouterHandler({
    url,
    dynamicRouteQuery: { category },
    routeToState(params) {
    
      
      
      return {
        [indexName]: {
          query: params.q ? params.q : null,
          refinementList: {
            root_types: decodeParam(params.subcategory, "root_types"),    
            brand: decodeParam(params.brand, "brand"),                
            category: decodeParam(params.strain, "category"),         
            feelings: decodeParam(params.effect, "feelings"),        
            available_weights: decodeParam(params.weight, "available_weights"),
            applicable_special_ids: decodeParam(params.special, "applicable_special_ids"),
          },
          range: {
            bucket_price: params.price ? params.price : null,
            percent_thc: params.thc ? params.thc : null,
            percent_cbd: params.cbd ? params.cbd : null,
          },
          page: params.page ? params.page : null,
          sortBy: params.order ? params.order : null,
        },
      };
    },

    stateToRoute(uiState) {
      const indexUiState = uiState[indexName];

      const serializeParam = (param, key) =>
        param
          ?.map((item) =>
            key === "brand" ? item : item.toLowerCase().replace(/ /g, "+") // **Skip transformation for brand**
          )
          .join(",");

      return {
        subcategory: serializeParam(indexUiState.refinementList?.root_types),
        brand: serializeParam(indexUiState.refinementList?.brand, "brand"), // **Pass "brand" to prevent encoding**
        strain: serializeParam(indexUiState.refinementList?.category),
        effect: serializeParam(indexUiState.refinementList?.feelings),
        weight: serializeParam(indexUiState.refinementList?.available_weights),
        special: serializeParam(
          indexUiState.refinementList?.applicable_special_ids
        ),
        price: indexUiState.range?.bucket_price,
        thc: indexUiState.range?.percent_thc,
        cbd: indexUiState.range?.percent_cbd,
        q: indexUiState.query,
        page: indexUiState.page,
        order: indexUiState.sortBy,
      };
    },
  });

  const { store } = useContext(StoreContext);
  const [list, setList] = useState(false);
  const { specials, loading, error, fetchAllSpecials } = useSpecials();
  const title = category.charAt(0).toUpperCase() + category.slice(1);
  const isBreakPoint = useMediaQuery(1200);

  // fetch product specials
  useEffect(() => {
    if (store) {
      fetchAllSpecials(store);
    }
  }, [store, fetchAllSpecials]);

  return (
    <>
      <Head>
        <title>{title ? title + " | Purple Lotus" : "| Purple Lotus"}</title>
        <meta
          property="og:title"
          content={`Shop Cannabis & Weed ${category}. Buy Weed Delivery Near Me.`}
          key="title"
        />
        <meta name="description" content={`Discover our assortment of weed ${category} & cannabis ${category} in a variety of blends and strains. Delivery available or pickup at Purple Lotus.`} />
      </Head>

      <InstantSearchSSRProvider {...serverState}>
        <InstantSearch
          searchClient={searchClient}
          indexName={indexName}
          initialUiState={initialUiState}
        >
          <NextRouterHandler />
          <Configure
            filters={`store_id:${store || initialStore} AND root_types:"${category}"`}
          // attributesToRetrieve={[
          //     "objectID",
          //     "product_id",
          //     "name",
          //     "brand",
          //     "image_urls",
          //   ]}
          />
          <header className={styles.header}>
            <Header
              url={`/shop/${category}`}
              category={category}
            />
            <VirtualSearchBox />
          </header>


          <ShopNav url={url} list={list} setList={setList} />

          <main className={!isBreakPoint ? "container" : undefined}>

            <div className={styles.stats}>
              <Stats />
              <CurrentRefinements productSpecials={specials} />
            </div>

            <div className={`${list ? productCardStyles.list : 'shop-product-grid'}`}>
              <NoResultsBoundary fallback={<NoResults />}>
                <Hits hitComponent={ProductCard} />
                <Pagination />
              </NoResultsBoundary>
            </div>

          </main>
        </InstantSearch>
      </InstantSearchSSRProvider>
    </>
  );
}

function CustomClearRefinements(props) {
  const { canRefine, refine } = useClearRefinements(props);

  if (canRefine) {
    return (
      <button className="button" data-type="basic" onClick={() => refine()}>
        Reset Filters {" "}
        <ArrowRotateLeft />
      </button>
    );
  }
}

function NoResultsBoundary({ children, fallback }) {
  const { results, status } = useInstantSearch();

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned yet.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  const { indexUiState } = useInstantSearch();

  return (
    <div className={styles.no_results}>
      <MessageXmark style={{fontSize:"3rem"}} />
      <span>
        {indexUiState.query ? (
          <span>
            No products match your search: <q>{indexUiState.query}</q>
          </span>
        ) : (
          "No products match your search criteria or are available at this location."
        )}
      </span>
      <CustomClearRefinements
        text="Clear all filters"
        includedAttributes={[
          "query",
          "category",
          "brand",
          "root_types",
          "feelings",
          "available_weights",
          "applicable_special_ids",
        ]}
      />
    </div>
  );
}

function VirtualSearchBox() {
  useSearchBox();
  return null;
}

function Stats() {
  const { scopedResults } = useInstantSearch();
  return (
    <>
      {scopedResults.map(({ indexId, results }) => (
        <span key={indexId} className={styles.productCount}>
          {results.nbHits === 0 ? "No Products" : `${results.nbHits} ${results.nbHits > 1 ? "Products" : "Product"}`}
        </span>
      ))}
    </>
  );
}